import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketButton, MarketField, MarketInputText } from '@market/react';
import BackButton from 'components/BackButton';
import ErrorToastSlot from 'components/ErrorToastSlot';
import { IdentifierType } from 'routes/profile/models/Identifier';
import { RequestStatus } from 'rpc/model/squareup/customers/request';
import {
  MAX_CODE_LENGTH,
  THROTTLE_TIMEOUT,
} from 'routes/native-sign-in/util/constants';
import useThrottle from 'utils/custom-react-hooks/useThrottle';
import {
  createReceiptLookupVerification,
  verifyReceiptLookupCode,
} from 'services/buyerportal';

type Props = {
  identifierType: IdentifierType;
  identifierValue: string;
  onClickBack: () => void;
  onVerified: () => void;
};

/**
 * Second step of the account-less receipt lookup: verifies the OTP. On success the backend sets the
 * `rlv` proof cookie and we advance to the search form. Modeled on
 * privacy-requests/delete/VerifyDeleteData, with SignInTotp's auto-submit-on-full-code UX.
 */
const ReceiptLookupOtpVerify: React.FC<Props> = ({
  identifierType,
  identifierValue,
  onClickBack,
  onVerified,
}) => {
  const { t } = useTranslation();

  const [code, setCode] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isUnexpectedError, setIsUnexpectedError] = useState<boolean>(false);
  const [isInvalidCode, setIsInvalidCode] = useState<boolean>(false);

  const verify = async () => {
    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);
      await verifyReceiptLookupCode(identifierType, identifierValue, code);
      onVerified();
    } catch (ex) {
      const status = (ex as { status?: RequestStatus }).status;
      if (status === RequestStatus.STATUS_BAD_REQUEST) {
        setIsInvalidCode(true);
        return;
      }
      setIsUnexpectedError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Auto-submit once a full-length code has been entered (mirrors SignInTotp).
  useEffect(() => {
    if (code.length === MAX_CODE_LENGTH && !isInvalidCode) {
      verify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const resend = async () => {
    try {
      setIsProcessing(true);
      setCode('');
      setIsInvalidCode(false);
      await createReceiptLookupVerification(identifierType, identifierValue);
    } catch {
      setIsUnexpectedError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const [throttledResend, isResendThrottled] = useThrottle(
    resend,
    THROTTLE_TIMEOUT
  );

  return (
    <div className={'flex justify-center mt-2 p-6'}>
      <main className={'flex flex-col max-w-[440px]'}>
        <BackButton onClick={onClickBack} />
        <h1 className={'heading-30 mb-[16px]'}>
          {t('common.verifyIdentifier.title', { identifierValue })}
        </h1>
        <p className={'paragraph-30 mt-0 mb-[24px]'}>
          {t('common.verifyIdentifier.subtitle')}
        </p>
        <MarketField invalid={isInvalidCode} className={'mb-[24px]'}>
          <MarketInputText>
            <label>{t('common.code.code')}</label>
            <input
              slot={'input'}
              data-testid={'code-input'}
              autoComplete={'one-time-code'}
              autoCorrect={'off'}
              autoCapitalize={'off'}
              inputMode={'numeric'}
              maxLength={MAX_CODE_LENGTH}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setIsInvalidCode(false);
              }}
              onKeyUpCapture={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  verify();
                }
              }}
            />
          </MarketInputText>
          <small slot={'error'}>{t('common.code.invalid')}</small>
        </MarketField>
        <MarketButton
          rank={'primary'}
          {...((isInvalidCode || isProcessing) && { disabled: true })}
          onClick={verify}
        >
          {t('receiptLookup.action.search')}
        </MarketButton>
        <MarketButton
          rank={'tertiary'}
          className={'mt-[8px]'}
          {...((isProcessing || isResendThrottled) && { disabled: true })}
          onClick={throttledResend}
        >
          {t('common.requestNewCode')}
        </MarketButton>
        <ErrorToastSlot
          errored={isUnexpectedError}
          clearError={() => setIsUnexpectedError(false)}
        />
      </main>
    </div>
  );
};

export default ReceiptLookupOtpVerify;
